"use client";

import React, { useState, useMemo } from "react";
import { ArrowRight, BookOpen, RefreshCw } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import Reveal from "@/components/common/Reveal";
import { PostsProvider } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { decodeHtmlEntities } from "@/lib/utils";
import { useCategoryPosts } from "@/hooks/useCategoryPosts";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
import type { Lang } from "@/lib/i18n";
interface CategoryOption {
  id: number | "all";
  name: Record<Lang, string>;
  categoryIds: number[];
}
const ALL_CATEGORY_IDS = [150, 48, 20, 168, 203];
const CATEGORIES: CategoryOption[] = [{
  id: "all",
  name: {
    en: "All",
    zh: "全部",
    es: "Todos",
    de: "Alle",
    fr: "Tous",
    pt: "Todos"
  },
  categoryIds: ALL_CATEGORY_IDS
}, {
  id: 150,
  name: {
    en: "Product Selection",
    zh: "产品选型",
    es: "Selección de productos",
    de: "Produktauswahl",
    fr: "Sélection de produits",
    pt: "Seleção de produtos"
  },
  categoryIds: [150]
}, {
  id: 48,
  name: {
    en: "Materials & Grades",
    zh: "材质等级",
    es: "Materiales y grados",
    de: "Werkstoffe & Güten",
    fr: "Matériaux et nuances",
    pt: "Materiais e graus"
  },
  categoryIds: [48]
}, {
  id: 20,
  name: {
    en: "Mesh & Filtration Fundamentals",
    zh: "网孔与过滤基础知识",
    es: "Fundamentos de malla y filtración",
    de: "Grundlagen zu Drahtgewebe & Filtration",
    fr: "Fondamentaux de la toile et de la filtration",
    pt: "Fundamentos de malha e filtração"
  },
  categoryIds: [20]
}, {
  id: 168,
  name: {
    en: "Specifications & Standards",
    zh: "规格与标准",
    es: "Especificaciones y normas",
    de: "Spezifikationen & Normen",
    fr: "Spécifications et normes",
    pt: "Especificações e normas"
  },
  categoryIds: [168]
}, {
  id: 203,
  name: {
    en: "Applications & Processes",
    zh: "应用与工艺流程",
    es: "Aplicaciones y procesos",
    de: "Anwendungen & Verfahren",
    fr: "Applications et procédés",
    pt: "Aplicações e processos"
  },
  categoryIds: [203]
}];
const CATEGORY_MAP: Record<number, Record<Lang, string>> = {
  150: {
    en: "Product Selection",
    zh: "产品选型",
    es: "Selección de productos",
    de: "Produktauswahl",
    fr: "Sélection de produits",
    pt: "Seleção de produtos"
  },
  48: {
    en: "Materials & Grades",
    zh: "材质等级",
    es: "Materiales y grados",
    de: "Werkstoffe & Güten",
    fr: "Matériaux et nuances",
    pt: "Materiais e graus"
  },
  20: {
    en: "Mesh & Filtration Fundamentals",
    zh: "网孔与过滤基础知识",
    es: "Fundamentos de malla y filtración",
    de: "Grundlagen zu Drahtgewebe & Filtration",
    fr: "Fondamentaux de la toile et de la filtration",
    pt: "Fundamentos de malha e filtração"
  },
  168: {
    en: "Specifications & Standards",
    zh: "规格与标准",
    es: "Especificaciones y normas",
    de: "Spezifikationen & Normen",
    fr: "Spécifications et normes",
    pt: "Especificações e normas"
  },
  203: {
    en: "Applications & Processes",
    zh: "应用与工艺流程",
    es: "Aplicaciones y procesos",
    de: "Anwendungen & Verfahren",
    fr: "Applications et procédés",
    pt: "Aplicações e processos"
  }
};
function stripHtml(html?: string): string {
  if (!html) return "";
  const stripped = html.replace(/<[^>]*>?/gm, "").trim();
  return decodeHtmlEntities(stripped);
}
function getCategoryBadgeLabel(postCategories: Array<{
  id: number | string;
  name?: string;
}> | undefined, lang: Lang): string {
  const fallbackLabel = pick(lang, {
    en: "Technical Article",
    zh: "技术文章",
    es: "Artículo técnico",
    de: "Technischer Artikel",
    fr: "Article technique",
    pt: "Artigo técnico"
  });
  if (!postCategories || postCategories.length === 0) {
    return fallbackLabel;
  }
  for (const cat of postCategories) {
    const numId = typeof cat.id === "number" ? cat.id : Number(cat.id);
    if (numId && CATEGORY_MAP[numId]) {
      return pick(lang, CATEGORY_MAP[numId]);
    }
  }
  const first = postCategories[0];
  if (first.name) {
    return decodeHtmlEntities(first.name);
  }
  return fallbackLabel;
}
function ArticlesConsumer({
  lang,
  categoryIds
}: {
  lang: Lang;
  categoryIds: number[];
}) {
  const {
    posts,
    totalFiltered,
    loading,
    error,
    refetch,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    currentPage,
    totalPages,
    ready
  } = useCategoryPosts({
    categoryIds,
    pageSize: 6
  });
  const loadErrorTitle = pick(lang, {
    en: "Unable to load articles.",
    zh: "无法加载文章。",
    es: "No se pudieron cargar los artículos.",
    de: "Artikel konnten nicht geladen werden.",
    fr: "Impossible de charger les articles.",
    pt: "Não foi possível carregar os artigos."
  });
  const loadErrorDesc = pick(lang, {
    en: "There was an issue fetching technical articles. Please try again.",
    zh: "获取技术文章时出现问题，请重试。",
    es: "Ocurrió un problema al obtener los artículos técnicos. Por favor, inténtelo de nuevo.",
    de: "Beim Abrufen der technischen Artikel ist ein Problem aufgetreten. Bitte versuchen Sie es erneut.",
    fr: "Un problème est survenu lors du chargement des articles techniques. Veuillez réessayer.",
    pt: "Ocorreu um problema ao carregar os artigos técnicos. Tente novamente."
  });
  const retryLabel = pick(lang, {
    en: "Retry",
    zh: "重试",
    es: "Reintentar",
    de: "Wiederholen",
    fr: "Réessayer",
    pt: "Tentar novamente"
  });
  const emptyTitle = pick(lang, {
    en: "No articles in this category yet.",
    zh: "该分类暂无文章。",
    es: "Aún no hay artículos en esta categoría.",
    de: "In dieser Kategorie sind noch keine Artikel vorhanden.",
    fr: "Aucun article dans cette catégorie pour le moment.",
    pt: "Ainda não há artigos nesta categoria."
  });
  const emptyDesc = pick(lang, {
    en: "Technical guides and engineering articles will appear here once published.",
    zh: "技术指南与工程文章发布后将在此处显示。",
    es: "Las guías técnicas y artículos de ingeniería aparecerán aquí una vez publicados.",
    de: "Technische Leitfäden und Fachartikel erscheinen hier, sobald sie veröffentlicht werden.",
    fr: "Les guides techniques et articles d'ingénierie apparaîtront ici dès leur publication.",
    pt: "Guias técnicos e artigos de engenharia aparecerão aqui assim que forem publicados."
  });
  const defaultExcerpt = pick(lang, {
    en: "Read technical mesh specifications, calculations, and filtration insights.",
    zh: "阅读技术丝网规格、工程计算与过滤技术见解。",
    es: "Lea especificaciones técnicas de mallas, cálculos y conocimientos de filtración.",
    de: "Lesen Sie technische Gewebespezifikationen, Berechnungen und Filtrationserkenntnisse.",
    fr: "Consultez les spécifications techniques de toiles, calculs et analyses de filtration.",
    pt: "Leia especificações técnicas de malhas, cálculos e análises de filtração."
  });
  const readArticle = pick(lang, {
    en: "Read article",
    zh: "阅读文章",
    es: "Leer artículo",
    de: "Artikel lesen",
    fr: "Lire l'article",
    pt: "Ler artigo"
  });
  const prevLabel = pick(lang, {
    en: "Previous",
    zh: "上一页",
    es: "Anterior",
    de: "Zurück",
    fr: "Précédent",
    pt: "Anterior"
  });
  const nextLabel = pick(lang, {
    en: "Next",
    zh: "下一页",
    es: "Siguiente",
    de: "Weiter",
    fr: "Suivant",
    pt: "Próximo"
  });
  if (loading || !ready) {
    return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({
        length: 6
      }).map((_, i) => <div key={i} data-index={i} className="flex flex-col gap-4 rounded-lg border border-border bg-card p-6">
            <Skeleton className="h-44 w-full rounded-md" />
            <div className="flex gap-2">
              <Skeleton className="h-4 w-20 rounded-md" />
              <Skeleton className="h-4 w-16 rounded-md" />
            </div>
            <Skeleton className="h-6 w-3/4 rounded-md" />
            <Skeleton className="h-16 w-full rounded-md" />
            <Skeleton className="h-9 w-28 rounded-md mt-auto" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-card p-12 text-center">
        <p className="text-sm font-medium text-destructive mb-3">
          {loadErrorTitle}
        </p>
        <p className="mf-caption text-muted-foreground mb-6 max-w-md">
          {loadErrorDesc}
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()} className="gap-2 min-h-[44px]">
          <RefreshCw className="size-4" aria-hidden="true" />
          {retryLabel}
        </Button>
      </div>;
  }
  if (totalFiltered === 0 || posts.length === 0) {
    return <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border bg-card p-12 text-center">
        <div className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary mb-3">
          <BookOpen className="size-5 text-primary" aria-hidden="true" />
        </div>
        <h3 className="text-base font-semibold text-foreground">
          {emptyTitle}
        </h3>
        <p className="text-sm text-muted-foreground mt-1 max-w-sm">
          {emptyDesc}
        </p>
      </div>;
  }
  return <div className="space-y-10">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {posts.map((post, i) => {
        const cleanExcerpt = stripHtml(post.excerpt);
        const categoryName = getCategoryBadgeLabel(post.categories, lang);
        const destination = post.link || `/TechnicalResourcesPage`;
        const defaultImageAlt = pick(lang, {
          en: "Technical article",
          zh: "技术文章",
          es: "Artículo técnico",
          de: "Technischer Fachartikel",
          fr: "Article technique",
          pt: "Artigo técnico"
        });
        return <Reveal key={post.id} delay={Math.min(i * 60, 300)}>
              <Card data-index={i} className="flex flex-col justify-between h-full rounded-lg border-border bg-card transition-[border-color] duration-200 hover:border-primary/40 mf-hover-lift overflow-hidden">
                <div>
                  {post.featuredImage && <div className="relative mf-card-media mf-ratio-card w-full overflow-hidden">
                      <img src={post.featuredImage} alt={post.title ? decodeHtmlEntities(post.title) : defaultImageAlt} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                    </div>}

                  <CardHeader className={post.featuredImage ? "pt-5 pb-3" : "pt-6 pb-3"}>
                    <div className="flex flex-wrap items-center gap-2 mb-2.5">
                      <Badge variant="outline" className="bg-muted text-foreground border-border mf-caption font-normal">
                        {categoryName}
                      </Badge>
                    </div>
                    <CardTitle className="mf-h3 text-foreground line-clamp-2">
                      <Link to={destination} className="text-foreground transition-colors hover:text-primary">
                        {decodeHtmlEntities(post.title)}
                      </Link>
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="pb-4">
                    {cleanExcerpt ? <p className="text-sm text-muted-foreground line-clamp-3 leading-relaxed">
                        {cleanExcerpt}
                      </p> : <p className="text-sm text-muted-foreground italic">
                        {defaultExcerpt}
                      </p>}
                  </CardContent>
                </div>

                <CardFooter className="pt-0 pb-6">
                  <Button variant="outline" size="sm" asChild className="w-full justify-between group min-h-[44px]">
                    <Link to={destination}>
                      <span>{readArticle}</span>
                      <ArrowRight className="size-3.5" aria-hidden="true" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </Reveal>;
      })}
      </div>

      {totalPages > 1 && <div className="flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-border pt-6">
          <p className="mf-caption text-muted-foreground">
            {pick(lang, {
          en: `Showing page ${currentPage} of ${totalPages}`,
          zh: `显示第 ${currentPage} 页 / 共 ${totalPages} 页`,
          es: `Mostrando página ${currentPage} de ${totalPages}`,
          de: `Seite ${currentPage} von ${totalPages} wird angezeigt`,
          fr: `Affichage de la page ${currentPage} sur ${totalPages}`,
          pt: `Mostrando página ${currentPage} de ${totalPages}`
        })}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => prevPage()} disabled={!hasPrev} className="min-h-[44px]">
              {prevLabel}
            </Button>
            <Button variant="outline" size="sm" onClick={() => nextPage()} disabled={!hasNext} className="min-h-[44px]">
              {nextLabel}
            </Button>
          </div>
        </div>}
    </div>;
}
export default function ArticlesLibrary() {
  const {
    lang
  } = useLanguage();
  const [selectedCategory, setSelectedCategory] = useState<number | "all">("all");
  const activeCategoryConfig = useMemo(() => {
    return CATEGORIES.find(c => c.id === selectedCategory) || CATEGORIES[0];
  }, [selectedCategory]);
  const wpQuery = useMemo(() => {
    return new WP_Query({
      post_type: "post",
      per_page: 100,
      order: "desc",
      orderby: "date",
      status: ["publish"],
      categories: activeCategoryConfig.categoryIds
    });
  }, [activeCategoryConfig]);
  const eyebrow = pick(lang, {
    en: "TECHNICAL ARTICLES",
    zh: "技术文章",
    es: "ARTÍCULOS TÉCNICOS",
    de: "TECHNISCHE ARTIKEL",
    fr: "ARTICLES TECHNIQUES",
    pt: "ARTIGOS TÉCNICOS"
  });
  const heading = pick(lang, {
    en: "Articles by Category",
    zh: "按分类浏览技术文章",
    es: "Artículos por categoría",
    de: "Artikel nach Kategorie",
    fr: "Articles par catégorie",
    pt: "Artigos por categoria"
  });
  const lead = pick(lang, {
    en: "Practical engineering guides on mesh selection, materials, standards and applications.",
    zh: "涵盖丝网选型、材质、标准与应用的工程实践指南。",
    es: "Guías prácticas de ingeniería sobre selección de mallas, materiales, normas y aplicaciones.",
    de: "Praxisorientierte Engineering-Leitfäden zu Gewebeauswahl, Werkstoffen, Normen und Anwendungen.",
    fr: "Guides pratiques d'ingénierie sur le choix des toiles, matériaux, normes et applications.",
    pt: "Guias práticos de engenharia sobre seleção de malhas, materiais, normas e aplicações."
  });
  const toolbarAria = pick(lang, {
    en: "Article category filter",
    zh: "文章分类过滤",
    es: "Filtro de categorías de artículos",
    de: "Filter für Artikelkategorien",
    fr: "Filtre de catégories d'articles",
    pt: "Filtro de categorias de artigos"
  });
  return <section id="articles" data-nav="light" className="w-full bg-background mf-section border-b border-border">
      <div className="mf-container">
        <Reveal className="flex flex-col gap-3 mb-8 md:mb-10">
          <span className="mf-caption uppercase tracking-wider text-muted-foreground font-mono">
            {eyebrow}
          </span>
          <h2 className="mf-h2 text-foreground">
            {heading}
          </h2>
          <p className="mf-lead text-muted-foreground max-w-2xl">
            {lead}
          </p>
        </Reveal>

        {/* Category filter buttons */}
        <Reveal delay={60}>
          <div className="flex flex-wrap items-center gap-2.5 mb-10" role="toolbar" aria-label={toolbarAria}>
            {CATEGORIES.map(cat => {
            const isSelected = selectedCategory === cat.id;
            return <Button key={String(cat.id)} type="button" variant={isSelected ? "default" : "outline"} aria-pressed={isSelected} onClick={() => setSelectedCategory(cat.id)} className="px-4 py-2 text-sm rounded-md transition-colors min-h-[44px]">
                  {pick(lang, cat.name)}
                </Button>;
          })}
          </div>
        </Reveal>

        {/* Live region for query results */}
        <div aria-live="polite">
          <PostsProvider key={String(selectedCategory)} wp_query={wpQuery}>
            <ArticlesConsumer lang={lang} categoryIds={activeCategoryConfig.categoryIds} />
          </PostsProvider>
        </div>
      </div>
    </section>;
}